import React from 'react';
import ReactMarkdown from 'react-markdown';
import type { Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkLinkify from 'remark-linkify';
import { List } from 'immutable';
import { applyStyling } from './message-utils';
import { Styling } from 'flow/types/Chats';
import { TypedMap } from 'flow/types/TypedMap';
import { ChatSessionKey } from 'flow/types/EntityKeys';

/**
 * Processes a message with both markdown and mentions.
 * This function intelligently handles the order of operations:
 * 1. First processes mentions to identify them
 * 2. Then processes markdown while preserving mention components
 */
export const processMarkdownWithMentions = (
    text: string,
    stylings: TypedMap<Styling>[],
    sessionKey: ChatSessionKey,
    markdownComponents: Components = {},
    hasMarkdownEnabled: boolean = true,
): React.ReactNode => {
    // If markdown is disabled, just apply styling without markdown processing
    if (!hasMarkdownEnabled) {
        return applyStyling(text, List(stylings || []), sessionKey);
    }

    if (!stylings || stylings.length === 0) {
        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkLinkify]}
                skipHtml={true}
                components={markdownComponents}
            >
                {text}
            </ReactMarkdown>
        );
    }

    // Convert stylings to immutable List for compatibility
    const styledElements = applyStyling(text, List(stylings), sessionKey);

    // If all elements are strings (no mentions), we can safely process as markdown
    const hasOnlyStrings = styledElements.every((el) => typeof el === 'string');

    if (hasOnlyStrings) {
        return (
            <ReactMarkdown
                remarkPlugins={[remarkGfm, remarkLinkify]}
                skipHtml={true}
                components={markdownComponents}
            >
                {text}
            </ReactMarkdown>
        );
    }

    return <>{styledElements}</>;
};
